import { logger } from './logger';
import api from './api';

/**
 * Mark a catalog item as DESTROYED.
 * The physical artwork has been destroyed but the catalog record is preserved.
 */
export const markCatalogItemDestroyed = async (
  catalogId: string,
  ownerId?: string | null
): Promise<Record<string, unknown>> => {
  try {
    const params = ownerId ? { owner_id: ownerId } : {};
    const response = await api.put(`/api/catalogs/${catalogId}/mark-destroyed`, {}, { params });
    return response.data;
  } catch (error) {
    logger.error('[Catalog Status API] Failed to mark item as destroyed', error);
    throw error;
  }
};

/**
 * Soft delete a catalog item (last catalog number only).
 * Sets isDeleted=true — the catalog record is preserved but hidden.
 * Only the highest-numbered catalog item can be deleted.
 */
export const deleteCatalogItem = async (
  catalogId: string,
  ownerId?: string | null
): Promise<Record<string, unknown>> => {
  try {
    const params = ownerId ? { owner_id: ownerId } : {};
    const response = await api.delete(`/api/catalogs/${catalogId}`, { params });
    return response.data;
  } catch (error) {
    logger.error('[Catalog Status API] Failed to delete catalog item', error);
    throw error;
  }
};

/**
 * Clear all data from a catalog item for reuse.
 * Preserves the catalog number slot but removes all metadata and renditions.
 * Available from the Review page only.
 */
export const clearCatalogItem = async (
  catalogId: string,
  ownerId?: string | null,
  password?: string
): Promise<Record<string, unknown>> => {
  try {
    const params = ownerId ? { owner_id: ownerId } : {};
    const body: Record<string, unknown> = {};
    if (password) {
      body.password = password;
    }
    const response = await api.put(`/api/catalogs/${catalogId}/clear`, body, { params });
    return response.data;
  } catch (error) {
    logger.error('[Catalog Status API] Failed to clear catalog item', error);
    throw error;
  }
};

export type RetirementAction = 'MARK_DESTROYED' | 'SOFT_DELETE';

export interface RetireResponse {
  message: string;
  status: string;
}

/**
 * Retire a catalog item with the specified action.
 */
export const retireCatalogItem = async (
  catalogId: string,
  action: RetirementAction
): Promise<RetireResponse> => {
  try {
    const body: Record<string, unknown> = { action };
    const response = await api.put(`/api/catalogs/${catalogId}/retire`, body);
    return response.data;
  } catch (error) {
    logger.error('[Catalog Status API] Failed to retire catalog item', error);
    throw error;
  }
};

/**
 * Restore a DESTROYED catalog item to READY status.
 */
export const restoreCatalogItem = async (
  catalogId: string,
  ownerId?: string | null
): Promise<Record<string, unknown>> => {
  try {
    const params = ownerId ? { owner_id: ownerId } : {};
    const response = await api.put(`/api/catalogs/${catalogId}/restore`, {}, { params });
    return response.data;
  } catch (error) {
    logger.error('[Catalog Status API] Failed to restore catalog item', error);
    throw error;
  }
};
